import React, { useState, useEffect, useMemo, useRef } from 'react';
import { useWizard, NamedPolicy, MethodMapping } from '../../contexts/WizardContext';
import { AVAILABLE_POLICIES } from '../../config/policies';
import { suggestPolicyName } from '../../utils/nameSuggestions';
import {
  EMAIL_DOMAINS_HINT,
  EMAIL_DOMAINS_PLACEHOLDER,
  formatAllowedEmails,
  validateEmailsOrDomains,
} from '../../utils/emailDomainValidation';
import ApproverEmailDisclaimer from '../ApproverEmailDisclaimer';
import FormattedMethodSignature from '../FormattedMethodSignature';
import { POLICY_GROUPS, policyGroupOf, PolicyGroupKey } from '../../config/policyGroups';
import './Step5PolicyMapping.css';

/** The kind of a policy of the wizard (its id, or the backend's type for an existing one). */
const policyKind = (p: NamedPolicy): PolicyGroupKey => policyGroupOf(p.apiType ?? p.policyType);

const Step5PolicyMapping: React.FC = () => {
  const { wizardData, updateWizardData } = useWizard();
  const [newPolicyName, setNewPolicyName] = useState('');
  const [selectedPolicyType, setSelectedPolicyType] = useState('');
  const [policyEmails, setPolicyEmails] = useState<string>('');
  const [emailError, setEmailError] = useState<string>('');
  const [validatorEmails, setValidatorEmails] = useState<string>('');
  const [validatorEmailError, setValidatorEmailError] = useState<string>('');
  const [requiredValidators, setRequiredValidators] = useState<string>('1');
  const [contractMethods, setContractMethods] = useState<string[]>([]);
  const policyNameTouchedRef = useRef(false);
  const lastPolicySuggestionRef = useRef('');

  // Parse ABI and extract public methods
  useEffect(() => {
    if (wizardData.contractABI) {
      try {
        const abi = JSON.parse(wizardData.contractABI);
        const methods = abi
          .filter((item: any) => item.type === 'function' && item.stateMutability !== 'view' && item.stateMutability !== 'pure')
          .map((item: any) => {
            const params = item.inputs?.map((input: any) => `${input.type} ${input.name || ''}`).join(', ') || '';
            return `${item.name}(${params})`;
          });
        setContractMethods(methods);

        // Initialize method mappings if not already done
        if (wizardData.methodMappings.length === 0) {
          const mappings: MethodMapping[] = methods.map((method: string) => ({
            methodName: method,
            policyId: null,
          }));
          updateWizardData({ methodMappings: mappings });
        }
      } catch (err) {
        console.error('Error parsing ABI:', err);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [wizardData.contractABI]);

  // Suggest policy name when type / dApp context changes (unless user edited it)
  useEffect(() => {
    if (!selectedPolicyType) {
      if (!policyNameTouchedRef.current) {
        setNewPolicyName('');
        lastPolicySuggestionRef.current = '';
      }
      return;
    }

    const suggestion = suggestPolicyName({
      policyType: selectedPolicyType,
      dAppName: wizardData.dAppName,
      email: wizardData.email,
      chain: wizardData.chain,
      contractAddress: wizardData.contractAddress,
      existingNames: wizardData.namedPolicies.map(p => p.name),
    });

    setNewPolicyName(prev => {
      const shouldApply =
        !policyNameTouchedRef.current ||
        prev.trim() === '' ||
        prev === lastPolicySuggestionRef.current;

      lastPolicySuggestionRef.current = suggestion;

      if (shouldApply && prev !== suggestion) {
        policyNameTouchedRef.current = false;
        return suggestion;
      }
      return prev;
    });
  }, [
    selectedPolicyType,
    wizardData.dAppName,
    wizardData.email,
    wizardData.chain,
    wizardData.contractAddress,
    wizardData.namedPolicies,
  ]);

  const handleEmailsChange = (value: string) => {
    setPolicyEmails(value);
    if (emailError) {
      setEmailError('');
    }
  };

  const handlePolicyNameChange = (value: string) => {
    policyNameTouchedRef.current = true;
    setNewPolicyName(value);
  };

  const selectedPolicyOption = useMemo(() => {
    return AVAILABLE_POLICIES.find(p => p.id === selectedPolicyType);
  }, [selectedPolicyType]);

  // Newly created this session first (newest on top), then existing server policies
  const sortedPolicies = useMemo(() => {
    const created = wizardData.namedPolicies.filter((p) => !p.isExisting).reverse();
    const existing = wizardData.namedPolicies.filter((p) => p.isExisting);
    return [...created, ...existing];
  }, [wizardData.namedPolicies]);

  /** The policies of each kind, newest first within a kind. */
  const groupedPolicies = useMemo(
    () => POLICY_GROUPS.map((g) => ({ ...g, policies: sortedPolicies.filter((p) => policyKind(p) === g.key) })),
    [sortedPolicies]
  );

  /** The options of a method's policy select, grouped and ordered as in Available Policies. */
  const renderPolicyOptions = () =>
    groupedPolicies
      .filter((g) => g.key === 'blocked' || g.policies.length > 0)
      .map((g) => (
        <optgroup key={g.key} label={g.title}>
          {g.key === 'blocked' && <option value="">No policy (all transactions blocked)</option>}
          {g.policies.map((policy) => (
            <option key={policy.id} value={policy.id}>
              {policy.name}
            </option>
          ))}
        </optgroup>
      ));

  const handleCreatePolicy = () => {
    if (!newPolicyName.trim() || !selectedPolicyType) {
      return;
    }

    if (selectedPolicyOption?.customizations.emails) {
      const validationError = validateEmailsOrDomains(policyEmails);
      if (validationError) {
        setEmailError(validationError);
        return;
      }
    }

    if (selectedPolicyOption?.customizations.multiAuth) {
      const required = Number.parseInt(requiredValidators, 10);
      if (!Number.isFinite(required) || required < 0 || !Number.isInteger(required)) {
        setValidatorEmailError('Enter a whole number of required validators (0 or more)');
        return;
      }
      if (required > 0) {
        const validationError = validateEmailsOrDomains(validatorEmails);
        if (validationError) {
          setValidatorEmailError(validationError);
          return;
        }
      } else if (validatorEmails.trim()) {
        const validationError = validateEmailsOrDomains(validatorEmails);
        if (validationError) {
          setValidatorEmailError(validationError);
          return;
        }
      }
    }

    const policy: NamedPolicy = {
      id: `policy-${Date.now()}`,
      name: newPolicyName.trim(),
      policyType: selectedPolicyType,
      customizations: {
        emails: selectedPolicyOption?.customizations.emails
          ? policyEmails.split(',').map((e) => e.trim()).filter((e) => e.length > 0)
          : undefined,
        ...(selectedPolicyOption?.customizations.multiAuth
          ? {
              validatorEmails: validatorEmails
                .split(',')
                .map((e) => e.trim())
                .filter((e) => e.length > 0),
              requiredAdditionalAuthentications: Number.parseInt(requiredValidators, 10) || 0,
            }
          : {}),
      },
    };

    updateWizardData({
      namedPolicies: [...wizardData.namedPolicies, policy],
    });

    policyNameTouchedRef.current = false;
    lastPolicySuggestionRef.current = '';
    setNewPolicyName('');
    setPolicyEmails('');
    setEmailError('');
    setValidatorEmails('');
    setValidatorEmailError('');
    setRequiredValidators('1');
  };

  const handleDeletePolicy = (policyId: string) => {
    const target = wizardData.namedPolicies.find((p) => p.id === policyId);
    if (target?.isExisting) {
      // Existing server policies stay available; only unassign from mappings
      const updatedMappings = wizardData.methodMappings.map((m) =>
        m.policyId === policyId ? { ...m, policyId: null } : m
      );
      updateWizardData({ methodMappings: updatedMappings });
      return;
    }
    const updatedPolicies = wizardData.namedPolicies.filter((p) => p.id !== policyId);
    const updatedMappings = wizardData.methodMappings.map((m) =>
      m.policyId === policyId ? { ...m, policyId: null } : m
    );
    updateWizardData({
      namedPolicies: updatedPolicies,
      methodMappings: updatedMappings,
    });
  };

  const handleMethodMappingChange = (methodName: string, policyId: string | null) => {
    const updatedMappings = wizardData.methodMappings.map(m =>
      m.methodName === methodName ? { ...m, policyId } : m
    );
    updateWizardData({ methodMappings: updatedMappings });
  };

  return (
    <div className="wizard-step step5-policy-mapping">
      <h2 className="wizard-step-title">Policy Creation & Method Mapping</h2>
      <p className="wizard-step-description">
        Reuse existing client policies or create new ones, then map them to your contract methods.
      </p>

      <div className="step5-split-container">
        <div className="step5-policy-creation">
          <h3 className="step5-section-title">Create Policies</h3>
          
          <div className="step5-policy-form">
            <div className="wizard-form-group">
              <label htmlFor="policy-type">Policy Type *</label>
              <select
                id="policy-type"
                value={selectedPolicyType}
                onChange={(e) => {
                  policyNameTouchedRef.current = false;
                  setSelectedPolicyType(e.target.value);
                }}
                title={selectedPolicyOption?.description || ''}
                className="step5-policy-type-select"
              >
                <option value="">Select a policy type</option>
                {AVAILABLE_POLICIES.map((policy) => (
                  <option key={policy.id} value={policy.id} title={policy.description}>
                    {policy.name}
                  </option>
                ))}
              </select>
              {selectedPolicyOption && (
                <div className="step5-policy-description">
                  <span className="step5-description-label">Description:</span>
                  <span className="step5-description-text">{selectedPolicyOption.description}</span>
                </div>
              )}
            </div>

            <div className="wizard-form-group">
              <label htmlFor="policy-name">Policy Name *</label>
              <input
                id="policy-name"
                type="text"
                value={newPolicyName}
                onChange={(e) => handlePolicyNameChange(e.target.value)}
                placeholder="Suggested from policy type and dApp name"
              />
              {!policyNameTouchedRef.current && newPolicyName && (
                <small className="step5-hint">Suggested name — edit freely if you prefer another.</small>
              )}
            </div>

            {selectedPolicyOption?.customizations.emails && (
              <div className="wizard-form-group">
                <label htmlFor="policy-emails">
                  {selectedPolicyOption.customizations.multiAuth
                    ? 'Initiator email domains/addresses (comma-separated) *'
                    : 'Email Domains/Addresses (comma-separated) *'}
                </label>
                <input
                  id="policy-emails"
                  type="text"
                  value={policyEmails}
                  onChange={(e) => handleEmailsChange(e.target.value)}
                  placeholder={EMAIL_DOMAINS_PLACEHOLDER}
                  className={emailError ? 'error' : ''}
                />
                {emailError && <div className="wizard-error">{emailError}</div>}
                <small className="step5-hint">{EMAIL_DOMAINS_HINT}</small>
                <ApproverEmailDisclaimer className="step5-hint" />
              </div>
            )}

            {selectedPolicyOption?.customizations.multiAuth && (
              <>
                <div className="wizard-form-group">
                  <label htmlFor="policy-required-validators">Required additional validators *</label>
                  <input
                    id="policy-required-validators"
                    type="number"
                    min={0}
                    step={1}
                    value={requiredValidators}
                    onChange={(e) => {
                      setRequiredValidators(e.target.value);
                      if (validatorEmailError) setValidatorEmailError('');
                    }}
                  />
                  <small className="step5-hint">
                    How many distinct validators must approve after the initiator authenticates.
                  </small>
                </div>
                <div className="wizard-form-group">
                  <label htmlFor="policy-validator-emails">
                    Validator email domains/addresses (comma-separated)
                    {Number.parseInt(requiredValidators, 10) > 0 ? ' *' : ''}
                  </label>
                  <input
                    id="policy-validator-emails"
                    type="text"
                    value={validatorEmails}
                    onChange={(e) => {
                      setValidatorEmails(e.target.value);
                      if (validatorEmailError) setValidatorEmailError('');
                    }}
                    placeholder={EMAIL_DOMAINS_PLACEHOLDER}
                    className={validatorEmailError ? 'error' : ''}
                  />
                  {validatorEmailError && <div className="wizard-error">{validatorEmailError}</div>}
                  <small className="step5-hint">
                    {EMAIL_DOMAINS_HINT} Full email addresses can be notified when approval is waiting;
                    domains or &quot;*&quot; only authorize without targeting a notification.
                  </small>
                  <ApproverEmailDisclaimer className="step5-hint" />
                </div>
              </>
            )}

            <button
              onClick={handleCreatePolicy}
              disabled={
                !newPolicyName.trim() ||
                !selectedPolicyType ||
                (selectedPolicyOption?.customizations.emails && !policyEmails.trim()) ||
                (selectedPolicyOption?.customizations.multiAuth &&
                  Number.parseInt(requiredValidators, 10) > 0 &&
                  !validatorEmails.trim())
              }
              className="step5-create-btn"
            >
              Create Policy
            </button>
          </div>

          <div className="step5-policies-list">
            <h4 className="step5-list-title">Available Policies</h4>
            {wizardData.namedPolicies.length === 0 && (
              <p className="step5-empty">No policies yet, create one above or add policies from the Policies page.</p>
            )}
            <div className="step5-policies">
              {groupedPolicies
                .filter((g) => g.key === 'blocked' || g.policies.length > 0)
                .map((g) => (
                  <div key={g.key} className="step5-policy-group">
                    <div className="step5-policy-group-title">{g.title}</div>
                    <div className="step5-policy-description-display">{g.description}</div>
                    {g.policies.length === 0 && g.key === 'blocked' && (
                      <div className="step5-hint">No blocked policy: choose "No policy" next to a method to block it.</div>
                    )}
                    {g.policies.map((policy) => (
                      <div key={policy.id} className="step5-policy-item">
                        <div className="step5-policy-header">
                          <span className="step5-policy-name">
                            {policy.name}
                            {policy.isExisting && (
                              <span className="dash-badge" style={{ marginLeft: '0.5rem' }}>
                                existing
                              </span>
                            )}
                          </span>
                          {!policy.isExisting && (
                            <button
                              onClick={() => handleDeletePolicy(policy.id)}
                              className="step5-delete-btn"
                              title="Delete policy"
                            >
                              <svg
                                width="16"
                                height="16"
                                viewBox="0 0 16 16"
                                fill="none"
                                xmlns="http://www.w3.org/2000/svg"
                                className="step5-delete-icon"
                              >
                                <path
                                  d="M2 4h12M5 4V2a1 1 0 0 1 1-1h4a1 1 0 0 1 1 1v2m3 0v10a1 1 0 0 1-1 1H3a1 1 0 0 1-1-1V4h12zM6 7v5M10 7v5"
                                  stroke="currentColor"
                                  strokeWidth="1.5"
                                  strokeLinecap="round"
                                  strokeLinejoin="round"
                                />
                              </svg>
                            </button>
                          )}
                        </div>
                        {g.key === 'otp' && (
                          <div className="step5-policy-emails">
                            <div>
                              <strong>
                                {policy.customizations.requiredAdditionalAuthentications != null ||
                                policy.customizations.validatorEmails
                                  ? 'Initiator:'
                                  : 'Allowed domains/addresses:'}
                              </strong>{' '}
                              {formatAllowedEmails(policy.customizations.emails)}
                            </div>
                            {(policy.customizations.requiredAdditionalAuthentications != null ||
                              (policy.customizations.validatorEmails &&
                                policy.customizations.validatorEmails.length > 0)) && (
                              <>
                                <div>
                                  <strong>Validators required:</strong>{' '}
                                  {policy.customizations.requiredAdditionalAuthentications ?? 0}
                                </div>
                                <div>
                                  <strong>Validator allowlist:</strong>{' '}
                                  {formatAllowedEmails(policy.customizations.validatorEmails)}
                                </div>
                              </>
                            )}
                          </div>
                        )}
                        {g.key === 'other' && (
                          <div className="step5-policy-details">
                            <span className="step5-policy-type">{policy.apiType || policy.policyType}</span>
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                ))}
            </div>
          </div>
        </div>

        <div className="step5-method-mapping">
          <h3 className="step5-section-title">Map Methods to Policies</h3>

          {contractMethods.length === 0 ? (
            <p className="step5-empty">No methods found. Please check your ABI in Step 4.</p>
          ) : (
            <div className="step5-methods-list">
              {contractMethods.map((method) => {
                const mapping = wizardData.methodMappings.find(m => m.methodName === method);
                return (
                  <div key={method} className="step5-method-item">
                    <div className="step5-method-name">
                      <FormattedMethodSignature method={method} />
                    </div>
                    <select
                      value={mapping?.policyId || ''}
                      onChange={(e) => handleMethodMappingChange(method, e.target.value || null)}
                      className="step5-method-select"
                    >
                      {renderPolicyOptions()}
                    </select>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default Step5PolicyMapping;
